import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate, useOutletContext } from "react-router";
import type { AppOutletContext } from "@/src/layouts/AppLayout";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { CountChip } from "@/components/count-chip";
import { Icon } from "@/components/icons";
import { PaginationFooter } from "@/components/pagination-footer";
import { Breadcrumb } from "@/components/breadcrumb";
import { StatCard } from "@/components/stat-card";
import { TableColumnsMenu, type ColumnMeta } from "@/components/table-columns-menu";
import { useTableColumnPrefs } from "@/components/use-table-column-prefs";
import {
  ColumnResizeHandle,
  buildResizableGridTemplate,
  resizeHandlerFor,
} from "@/components/column-resize";
import type { Batch, BatchRow } from "../../../_data/batches";
import { formatDate, formatPercent } from "../../../_lib/format";
import { isoCountryName, toIsoCode } from "../../../_lib/iso-countries";
import { ScoreChip } from "@/components/score-chip";
import { SourceChips, CountryChips } from "@/components/table-chips";
import { Button } from "@/components/button";
import { getBatch, addBatchRowToMonitoring } from "@/src/services/batch_service";
import NotFoundPage from "@/src/pages/_errors/NotFoundPage";
import { RowActionsMenu, type RowAction } from "@/components/row-actions-menu";
import { useToast } from "@/components/action-feedback";
import { toUserMessage } from "@/src/services/error_messages";

const COLUMN_IDS = [
  "name",
  "identifier",
  "externalId",
  "type",
  "dob",
  "country",
  "minScore",
  "source",
  "matches",
] as const;
type BatchColumnId = (typeof COLUMN_IDS)[number];

type SortKey = BatchColumnId;
type SortDir = "asc" | "desc";
type SortState = { key: SortKey; dir: SortDir };

const DEFAULT_SORT: SortState = { key: "minScore", dir: "desc" };

type Column = {
  id: BatchColumnId;
  header: string;
  width: string;
  cell: (row: BatchRow) => ReactNode;
};

const COLUMNS_BY_ID: Record<BatchColumnId, Column> = {
  name: {
    id: "name",
    header: "Name",
    width: "minmax(180px,2fr)",
    cell: (r) => <span className="block truncate text-black">{r.name}</span>,
  },
  identifier: {
    id: "identifier",
    header: "Identifier",
    width: "minmax(140px,1fr)",
    cell: (r) => <span className="block truncate text-black">{r.identifier ?? "—"}</span>,
  },
  externalId: {
    id: "externalId",
    header: "External ID",
    width: "minmax(120px,1fr)",
    cell: (r) => <span className="block truncate text-black">{r.externalId || "—"}</span>,
  },
  type: {
    id: "type",
    header: "Type",
    width: "110px",
    cell: (r) => <span>{r.entityType}</span>,
  },
  country: {
    id: "country",
    header: "Country",
    width: "minmax(110px,1fr)",
    cell: (r) => <CountryChips countries={r.country} />,
  },
  dob: {
    id: "dob",
    header: "DOB",
    width: "110px",
    cell: (r) => <span>{formatDate(r.dateOfBirth)}</span>,
  },
  minScore: {
    id: "minScore",
    header: "Min. score",
    width: "110px",
    cell: (r) => <ScoreChip score={r.minScore} />,
  },
  source: {
    id: "source",
    header: "Watchlist",
    width: "140px",
    cell: (r) => <SourceChips sources={r.dataSource} />,
  },
  matches: {
    id: "matches",
    header: "Matches",
    width: "120px",
    cell: (r) => <CountChip count={r.hits} />,
  },
};

const REQUIRED_COLUMNS: ReadonlySet<BatchColumnId> = new Set<BatchColumnId>(["name"]);

const LOCKED_FIRST: readonly BatchColumnId[] = ["name"];
const LOCKED_FIRST_SET = new Set<string>(LOCKED_FIRST);

const BATCH_COLUMNS_META: readonly ColumnMeta[] = COLUMN_IDS.map((id) => ({
  id,
  label: COLUMNS_BY_ID[id].header,
  required: REQUIRED_COLUMNS.has(id),
}));

function visibleColumns(
  order: readonly BatchColumnId[],
  hidden: ReadonlySet<BatchColumnId>,
): Column[] {
  return order.filter((id) => !hidden.has(id)).map((id) => COLUMNS_BY_ID[id]);
}

const buildGridTemplate = buildResizableGridTemplate<BatchColumnId, Column>;

const ACTIONS_COL_WIDTH = "48px";

type LoadStatus = "loading" | "error" | "ready";

export function BatchResults({ batchId }: { batchId: string }) {
  const [status, setStatus] = useState<LoadStatus>("loading");
  const [batch, setBatch] = useState<Batch | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const {
    order,
    hidden,
    rowsPerPage,
    widths,
    setOrder,
    toggleHidden,
    setRowsPerPage,
    setWidth,
    reset,
  } = useTableColumnPrefs<BatchColumnId>("batch-results", {
    columnIds: COLUMN_IDS,
    lockedFirst: LOCKED_FIRST,
  });
  const [page, setPage] = useState(1);

  const navigate = useNavigate();
  const ctx = useOutletContext<AppOutletContext | null>();
  const enableMonitoring = ctx?.enableMonitoring ?? true;
  const showToast = useToast();
  const [addingRowId, setAddingRowId] = useState<string | null>(null);

  const handleAddToMonitoring = useCallback(
    async (row: BatchRow) => {
      if (addingRowId) return;
      setAddingRowId(row.id);
      const res = await addBatchRowToMonitoring(row);
      setAddingRowId(null);
      if (!res.ok) {
        showToast({ tone: "error", text: toUserMessage(res.error) });
        return;
      }
      navigate("/monitoring-portal");
    },
    [addingRowId, navigate, showToast],
  );

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    setStatus("loading");
    setNotFound(false);
    setPage(1);
    const res = await getBatch(batchId);
    if (res.ok) {
      setBatch(res.value);
      setStatus("ready");
    } else {
      setNotFound(res.error.code === "not_found");
      setStatus("error");
    }
  }, [batchId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (status !== "ready" || !batch?.processing) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }
    pollRef.current = setInterval(() => {
      void getBatch(batchId).then((res) => {
        if (!res.ok) return;
        setBatch(res.value);
        if (!res.value.processing) {
          clearInterval(pollRef.current!);
          pollRef.current = null;
        }
      });
    }, 3_000);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [status, batch?.processing, batchId]);

  const rows = batch?.rows ?? [];
  const sorted = useMemo(() => sortRows(rows, sort), [rows, sort]);

  const total = rows.length;
  const matched = rows.filter((r) => r.hits > 0).length;
  const matchRate = total === 0 ? 0 : (matched / total) * 100;
  const individuals = rows.filter((r) => r.entityType === "Individual").length;
  const organizations = rows.filter((r) => r.entityType === "Organization").length;

  const pageCount = Math.max(1, Math.ceil(total / rowsPerPage));
  const safePage = Math.min(page, pageCount);
  const pageStart = (safePage - 1) * rowsPerPage;
  const pageRows = sorted.slice(pageStart, pageStart + rowsPerPage);

  if (status === "error" && notFound) return <NotFoundPage />;

  return (
    <div className="flex min-h-screen flex-1 flex-col bg-white px-4 pb-4 sm:px-8">
      <div className="flex flex-col gap-2 pt-4">
        <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 flex-1 items-center">
            <Breadcrumb
              backHref="/batch"
              segments={[
                { label: "Batch Screening", href: "/batch" },
                { label: batch?.filename ?? "…", current: true },
              ]}
            />
          </div>
          {status === "ready" && !batch?.processing && batch?.downloadUrl && (
            <Button
              hierarchy="tertiary"
              size="sm"
              leftIcon={<Icon name="download" size={20} weight={300} />}
              href={batch.downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Download results
            </Button>
          )}
        </div>
        {status === "loading" ? (
          <BatchSummarySkeleton />
        ) : status === "ready" ? (
          <BatchFileSummary batch={batch!} total={total} matched={matched} matchRate={matchRate} />
        ) : null}
      </div>

      {status === "error" ? (
        <BatchResultsErrorState onRetry={load} />
      ) : status === "loading" ? (
        <BatchTableSkeleton />
      ) : batch?.processing ? (
        <BatchProcessingState />
      ) : (
        <>
          <div className="mt-2 flex min-h-0 flex-col overflow-hidden rounded-[4px] border border-grey-200 bg-white lg:flex-1">
            <div className="flex items-center justify-between gap-2 px-4 py-3">
              <h2 className="text-h4 text-black">Searches ({total})</h2>
              <TableColumnsMenu
                columns={BATCH_COLUMNS_META}
                hidden={hidden}
                onToggle={(id) => toggleHidden(id as BatchColumnId)}
                onReset={reset}
              />
            </div>
            <div className="max-h-[420px] overflow-auto lg:max-h-none lg:min-h-0 lg:flex-1">
              <div className="sticky top-0 z-[1]">
                <ColumnHeaders
                  sort={sort}
                  onSort={(next) => {
                    setSort(next);
                    setPage(1);
                  }}
                  order={order}
                  hidden={hidden}
                  onReorder={setOrder}
                  widths={widths}
                  onResizeColumn={setWidth}
                />
              </div>
              <Table
                batch={batch!}
                rows={pageRows}
                order={order}
                hidden={hidden}
                widths={widths}
                onAddToMonitoring={handleAddToMonitoring}
                addingRowId={addingRowId}
                enableMonitoring={enableMonitoring}
              />
            </div>
          </div>
          <div className="mt-auto sticky bottom-0 z-10 bg-white pt-2">
            <PaginationFooter
              total={total}
              individuals={individuals}
              organizations={organizations}
              others={0}
              totalLabel="entities"
              page={safePage}
              pageCount={pageCount}
              onPageChange={setPage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(n) => {
                setRowsPerPage(n);
                setPage(1);
              }}
            />
          </div>
        </>
      )}
    </div>
  );
}

function BatchFileSummary({
  batch,
  total,
  matched,
  matchRate,
}: {
  batch: Batch;
  total: number;
  matched: number;
  matchRate: number;
}) {
  const withMatches = batch.rows.filter((r) => r.hits > 0).length;
  return (
    <>
      <div className="flex flex-col gap-1">
        <span className="text-small font-medium text-grey-600">Batch file</span>
        <div className="flex w-full items-center gap-4 rounded-[4px] border border-green-500 bg-green-100 px-4 py-2">
          <img src="/brand/File.svg" alt="" aria-hidden className="h-[54px] w-auto shrink-0" />
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="truncate text-small font-medium text-green-900">{batch.filename}</span>
            <span className="truncate text-small text-green-700">
              {formatDate(batch.uploadedAt)} · {total} {total === 1 ? "name" : "names"} ·{" "}
              {withMatches} with matches
            </span>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
        <StatCard icon="description" label="Screened names" value={total.toLocaleString()} />
        <StatCard icon="library_add_check" label="Matched names" value={matched.toLocaleString()} />
        <StatCard icon="percent" label="Match rate" value={formatPercent(matchRate)} />
      </div>
    </>
  );
}

function BatchSummarySkeleton() {
  return (
    <div className="flex flex-col gap-2" aria-busy="true">
      <div className="h-[70px] w-full animate-pulse rounded-[4px] bg-grey-100" />
      <div className="flex flex-col gap-2 sm:flex-row">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-[76px] flex-1 animate-pulse rounded-[4px] bg-grey-100" />
        ))}
      </div>
    </div>
  );
}

function BatchTableSkeleton() {
  return (
    <div className="mt-8 flex min-h-0 flex-1 flex-col gap-2" aria-busy="true">
      <div className="h-6 w-32 animate-pulse rounded bg-grey-100" />
      <div className="flex flex-col gap-px overflow-hidden rounded-[4px] border border-grey-200">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-12 w-full animate-pulse bg-grey-50" />
        ))}
      </div>
    </div>
  );
}

function BatchProcessingState() {
  return (
    <div className="mt-8 flex flex-1 flex-col items-center justify-center gap-3 rounded-[4px] border border-grey-200 bg-white px-4 py-12 text-center shadow-flat">
      <div className="size-8 animate-spin rounded-full border-[3px] border-green-200 border-t-green-500" />
      <div className="flex flex-col gap-0.5">
        <p className="text-body font-medium text-black">Processing your batch</p>
        <p className="text-small text-grey-900">
          This usually takes a few seconds. Results will appear automatically.
        </p>
      </div>
    </div>
  );
}

function BatchResultsErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="mt-8 flex flex-1 flex-col items-center justify-center gap-3 rounded-[4px] border border-grey-200 bg-white px-4 py-12 text-center shadow-flat">
      <div className="flex size-9 items-center justify-center rounded-[4px] bg-red-100 text-red-600">
        <Icon name="error" size={20} />
      </div>
      <div className="flex flex-col gap-0.5">
        <p className="text-body font-medium text-black">Couldn&apos;t load this batch</p>
        <p className="text-small text-grey-900">Something went wrong fetching the batch results.</p>
      </div>
      <Button
        hierarchy="secondary"
        size="sm"
        leftIcon={<Icon name="refresh" size={20} />}
        onClick={onRetry}
      >
        Retry
      </Button>
    </div>
  );
}

function ColumnHeaders({
  sort,
  onSort,
  order,
  hidden,
  onReorder,
  widths,
  onResizeColumn,
}: {
  sort: SortState;
  onSort: (s: SortState) => void;
  order: readonly BatchColumnId[];
  hidden: ReadonlySet<BatchColumnId>;
  onReorder: (next: BatchColumnId[]) => void;
  widths?: Partial<Record<BatchColumnId, number>>;
  onResizeColumn?: (id: BatchColumnId, px: number) => void;
}) {
  function toggle(key: SortKey) {
    if (sort.key === key) {
      onSort({ key, dir: sort.dir === "asc" ? "desc" : "asc" });
    } else {
      onSort({ key, dir: "desc" });
    }
  }

  const columns = visibleColumns(order, hidden);
  const visibleIds = columns.map((c) => c.id);
  const draggableIds = visibleIds.filter((id) => !LOCKED_FIRST_SET.has(id));
  const gridTemplate = `${buildGridTemplate(columns, widths)} ${ACTIONS_COL_WIDTH}`;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    if (LOCKED_FIRST_SET.has(active.id as string)) return;
    const oldIndex = order.indexOf(active.id as BatchColumnId);
    const rawNewIndex = order.indexOf(over.id as BatchColumnId);
    if (oldIndex < 0 || rawNewIndex < 0) return;
    const newIndex = Math.max(LOCKED_FIRST.length, rawNewIndex);
    if (oldIndex === newIndex) return;
    onReorder(arrayMove(order as BatchColumnId[], oldIndex, newIndex));
  }

  const headerGrid = (
    <div
      className="grid h-9 items-center gap-3 border-y border-grey-200 bg-grey-50 px-4 text-tiny font-medium text-grey-900"
      style={{ gridTemplateColumns: gridTemplate }}
    >
      {columns.map((column, index) => {
        const resizable = resizeHandlerFor(index, columns.length, onResizeColumn);
        return !LOCKED_FIRST_SET.has(column.id) ? (
          <SortableHeaderCell
            key={column.id}
            column={column}
            sort={sort}
            onSort={toggle}
            onResizeColumn={resizable}
          />
        ) : (
          <StaticHeaderCell
            key={column.id}
            column={column}
            sort={sort}
            onSort={toggle}
            locked={LOCKED_FIRST_SET.has(column.id)}
            onResizeColumn={resizable}
          />
        );
      })}
      <div aria-hidden className="h-full" />
    </div>
  );

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={draggableIds} strategy={horizontalListSortingStrategy}>
        {headerGrid}
      </SortableContext>
    </DndContext>
  );
}

function SortableHeaderCell({
  column,
  sort,
  onSort,
  onResizeColumn,
}: {
  column: Column;
  sort: SortState;
  onSort: (key: SortKey) => void;
  onResizeColumn?: (id: BatchColumnId, px: number) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: column.id,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-column-id={column.id}
      className="relative flex h-full items-center gap-2 select-none"
    >
      <DragHandle attributes={attributes} listeners={listeners} />
      <HeaderLabel column={column} sort={sort} onSort={onSort} />
      {onResizeColumn && <ColumnResizeHandle columnId={column.id} onResize={onResizeColumn} />}
    </div>
  );
}

function StaticHeaderCell({
  column,
  sort,
  onSort,
  locked = false,
  onResizeColumn,
}: {
  column: Column;
  sort: SortState;
  onSort: (key: SortKey) => void;
  locked?: boolean;
  onResizeColumn?: (id: BatchColumnId, px: number) => void;
}) {
  return (
    <div data-column-id={column.id} className="relative flex h-full items-center gap-2">
      {!locked && <span className="h-5 w-2 shrink-0" aria-hidden />}
      <HeaderLabel column={column} sort={sort} onSort={onSort} />
      {onResizeColumn && <ColumnResizeHandle columnId={column.id} onResize={onResizeColumn} />}
    </div>
  );
}

function DragHandle({
  attributes,
  listeners,
}: {
  attributes: ReturnType<typeof useSortable>["attributes"];
  listeners: ReturnType<typeof useSortable>["listeners"];
}) {
  return (
    <button
      type="button"
      aria-label="Drag column to reorder"
      {...attributes}
      {...listeners}
      className="flex h-5 w-2 shrink-0 cursor-grab items-center justify-center text-grey-300 hover:text-grey-900 active:cursor-grabbing touch-none"
    >
      <DragHandleIcon />
    </button>
  );
}

function DragHandleIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="8"
      height="20"
      viewBox="0 0 8 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <circle cx="2" cy="6" r="1.25" />
      <circle cx="6" cy="6" r="1.25" />
      <circle cx="2" cy="10" r="1.25" />
      <circle cx="6" cy="10" r="1.25" />
      <circle cx="2" cy="14" r="1.25" />
      <circle cx="6" cy="14" r="1.25" />
    </svg>
  );
}

function HeaderLabel({
  column,
  sort,
  onSort,
}: {
  column: Column;
  sort: SortState;
  onSort: (key: SortKey) => void;
}) {
  const active = sort.key === column.id;
  const iconName = !active ? "unfold_more" : sort.dir === "asc" ? "arrow_upward" : "arrow_downward";
  return (
    <button
      type="button"
      onClick={() => onSort(column.id)}
      aria-label={
        active
          ? `${column.header}, sorted ${sort.dir === "asc" ? "ascending" : "descending"}`
          : `Sort by ${column.header}`
      }
      className={`inline-flex items-center gap-1 transition-colors ${
        active ? "text-black" : "hover:text-black"
      }`}
    >
      <span>{column.header}</span>
      <Icon name={iconName} size={20} />
    </button>
  );
}

function Table({
  batch,
  rows,
  order,
  hidden,
  widths,
  onAddToMonitoring,
  addingRowId,
  enableMonitoring,
}: {
  batch: Batch;
  rows: BatchRow[];
  order: readonly BatchColumnId[];
  hidden: ReadonlySet<BatchColumnId>;
  widths?: Partial<Record<BatchColumnId, number>>;
  onAddToMonitoring: (row: BatchRow) => void;
  addingRowId: string | null;
  enableMonitoring: boolean;
}) {
  const navigate = useNavigate();
  const columns = visibleColumns(order, hidden);
  const gridTemplate = `${buildGridTemplate(columns, widths)} ${ACTIONS_COL_WIDTH}`;

  function open(row: BatchRow) {
    navigate(`/batch/${batch.id}/search/${row.id}`);
  }

  return (
    <div className="flex flex-col bg-white">
      {rows.map((row) => {
        const actions = rowActions(
          row,
          onAddToMonitoring,
          addingRowId === row.id,
          enableMonitoring,
        );
        return (
          <div
            key={row.id}
            role="button"
            tabIndex={0}
            onClick={() => open(row)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                open(row);
              }
            }}
            style={{ gridTemplateColumns: gridTemplate }}
            className="grid h-12 cursor-pointer items-center gap-3 border-b border-grey-100 px-4 text-left text-small text-black transition-colors hover:bg-grey-50"
          >
            {columns.map((column) => (
              <span key={column.id} className="min-w-0">
                {column.cell(row)}
              </span>
            ))}
            <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
              {actions.length > 0 && (
                <RowActionsMenu actions={actions} label={`Actions for ${row.name}`} />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function rowActions(
  row: BatchRow,
  onAddToMonitoring: (row: BatchRow) => void,
  adding: boolean,
  enableMonitoring: boolean,
): RowAction[] {
  if (!enableMonitoring) return [];
  return [
    {
      label: "Add to monitoring",
      icon: "bookmark_add",
      onClick: () => onAddToMonitoring(row),
      disabled: adding,
      disabledReason: "Adding to monitoring…",
    },
  ];
}

function sortRows(rows: BatchRow[], { key, dir }: SortState): BatchRow[] {
  const sign = dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const va = sortValue(a, key);
    const vb = sortValue(b, key);
    const aEmpty = va === null || va === "";
    const bEmpty = vb === null || vb === "";
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;
    if (bEmpty) return -1;
    if (va === vb) return 0;
    return va < vb ? -1 * sign : 1 * sign;
  });
}

function sortValue(row: BatchRow, key: SortKey): string | number | null {
  switch (key) {
    case "name":
      return row.name;
    case "identifier":
      return row.identifier;
    case "externalId":
      return row.externalId || null;
    case "type":
      return row.entityType;
    case "dob":
      return row.dateOfBirth;
    case "country": {
      if (row.country.length === 0) return null;
      const names = row.country.map((raw) => {
        const iso = toIsoCode(raw);
        return iso ? isoCountryName(iso) : raw;
      });
      return names.join(", ");
    }
    case "minScore":
      return row.minScore;
    case "source":
      return row.dataSource.length > 0 ? row.dataSource.join(", ") : null;
    case "matches":
      return row.hits;
  }
}
